/**
 * RechercheView
 * Two-step search flow for Facturation:
 * 1. Select search mode (Recent/Affaire/Client)
 * 2. After search, show Factures or Notes de crédit based on parent's activeSubTab
 */

import React, { useState, useEffect } from 'react';
import {
  DocumentTextIcon,
  DocumentPlusIcon,
  DocumentMinusIcon,
} from '@heroicons/react/24/outline';
import { SearchModeSelector, type SearchMode } from '../shared';
import { InvoicesListView } from '../InvoicesView/InvoicesListView';
import { CreditNotesListView } from '../CreditNotesView/CreditNotesListView';
import { ProductsEmbeddedView } from '../ProductsEmbeddedView/ProductsEmbeddedView';
import { CreateCustomInvoiceModal } from '../CustomInvoice/CreateCustomInvoiceModal';
import { CreateCustomCreditNoteModal } from '../CustomCreditNote/CreateCustomCreditNoteModal';
import type { InvoiceFilters } from '../../types/facturation.types';
import '../../facturation.css';

export type RechercheSubTab = 'factures' | 'notes-credit' | 'produits';

interface RechercheViewProps {
  activeSubTab: RechercheSubTab;
  onSearchStateChange: (isInResults: boolean, selectedAffaireId?: string | null) => void;
  preselectedAffaireId?: number | null;
  onPreselectedAffaireUsed?: () => void;
  onAffaireSelected?: (affaireId: string | null) => void;
}

export const RechercheView: React.FC<RechercheViewProps> = ({
  activeSubTab,
  onSearchStateChange,
  preselectedAffaireId,
  onPreselectedAffaireUsed,
  onAffaireSelected,
}) => {
  // Step state: 'search' or 'results'
  const [step, setStep] = useState<'search' | 'results'>('search');

  // Search state
  const [searchMode, setSearchMode] = useState<SearchMode>('recent');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearchLabel, setActiveSearchLabel] = useState('');

  // Custom document modal states
  const [showCustomInvoiceModal, setShowCustomInvoiceModal] = useState(false);
  const [showCustomCreditNoteModal, setShowCustomCreditNoteModal] = useState(false);

  // Filters passed to child views
  const [filters, setFilters] = useState<InvoiceFilters>({
    page: 1,
    limit: 20,
    sort_by: 'created_at',
    sort_order: 'DESC',
  });

  // Handle mode change
  const handleModeChange = (mode: SearchMode) => {
    setSearchMode(mode);
    setSearchTerm('');
  };

  // Handle search / selection
  const handleSearch = () => {
    if (searchMode === 'recent') {
      setActiveSearchLabel('20 dernieres factures');
      setFilters({
        page: 1,
        limit: 20,
        sort_by: 'created_at',
        sort_order: 'DESC',
        search: undefined,
      });
      setStep('results');
      onAffaireSelected?.(null); // No specific affaire for recent mode
      onSearchStateChange(true, null); // Called after onAffaireSelected so parent knows context
    } else if (searchTerm.trim()) {
      // Normalize: for facture mode, convert "2026/00971" format to "2026-00971" to match DB
      // Only convert if it looks like an invoice number (YYYY/NNNNN), not a structured communication
      let normalizedTerm = searchTerm.trim();
      if (searchMode === 'facture' && /^\d{4}\//.test(normalizedTerm)) {
        normalizedTerm = normalizedTerm.replace(/\//g, '-');
      }

      const labels: Record<string, string> = {
        affaire: `Affaire: ${searchTerm}`,
        facture: `Facture: ${normalizedTerm}`,
        client: `Client: ${searchTerm}`,
      };
      setActiveSearchLabel(labels[searchMode] || searchTerm);
      setFilters({
        page: 1,
        limit: 25,
        sort_by: 'created_at',
        sort_order: 'DESC',
        search: normalizedTerm,
      });
      setStep('results');
      // Set affaireId BEFORE search state change so parent can determine default tab
      const selectedAffaire = searchMode === 'affaire' ? searchTerm.trim() : null;
      onAffaireSelected?.(selectedAffaire);
      onSearchStateChange(true, selectedAffaire);
    }
  };

  // Go back to search (called from parent via exposed method or when "Retour" is clicked in toggle)
  const handleBack = () => {
    setStep('search');
    setSearchTerm('');
    onSearchStateChange(false);
  };

  // Expose handleBack to parent - we'll do this via a callback approach
  // When activeSubTab becomes undefined/back, we go back
  React.useEffect(() => {
    // This is handled by the parent now
  }, [activeSubTab]);

  // Handle pre-selected affaire from navigation (e.g., from Tracker page)
  useEffect(() => {
    if (preselectedAffaireId) {
      // Set search mode to affaire and auto-search
      setSearchMode('affaire');
      setSearchTerm(String(preselectedAffaireId));
      setActiveSearchLabel(`Affaire: ${preselectedAffaireId}`);
      setFilters({
        page: 1,
        limit: 25,
        sort_by: 'created_at',
        sort_order: 'DESC',
        search: String(preselectedAffaireId),
      });
      setStep('results');
      // Notify parent of the selected affaire BEFORE search state change
      onAffaireSelected?.(String(preselectedAffaireId));
      onSearchStateChange(true, String(preselectedAffaireId));
      // Notify parent that we've used the pre-selected affaire
      onPreselectedAffaireUsed?.();
    }
  }, [preselectedAffaireId, onSearchStateChange, onPreselectedAffaireUsed, onAffaireSelected]);

  // STEP 1: Search mode selection
  if (step === 'search') {
    return (
      <div className="fact-recherche">
        <div className="flex flex-col items-center justify-center min-h-[500px] p-8">
          <div className="w-full max-w-2xl">
            <div className="text-center mb-8">
              <DocumentTextIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Rechercher des factures</h2>
              <p className="text-gray-500">Selectionnez un mode de recherche pour acceder aux factures et notes de credit</p>
            </div>

            <SearchModeSelector
              mode={searchMode}
              onModeChange={handleModeChange}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              onSearch={handleSearch}
              isLoading={false}
            />

            {searchMode === 'recent' && (
              <div className="fact-search-mode-action">
                <button
                  onClick={handleSearch}
                  className="fact-search-mode-action__btn"
                >
                  Voir les 20 dernieres factures
                </button>
              </div>
            )}

            {/* Quick actions: Create custom invoice or credit note (no affaire search needed) */}
            <div className="fact-quick-action-divider">
              <span>ou creer un document personnalise</span>
            </div>
            <div className="fact-quick-actions-row">
              <div className="fact-quick-action">
                <button
                  onClick={() => setShowCustomInvoiceModal(true)}
                  className="fact-quick-action__btn"
                >
                  <DocumentPlusIcon className="fact-quick-action__icon" />
                  <span>Facture personnalisee</span>
                </button>
                <p className="fact-quick-action__hint">Sans lien avec une affaire</p>
              </div>
              <div className="fact-quick-action">
                <button
                  onClick={() => setShowCustomCreditNoteModal(true)}
                  className="fact-quick-action__btn fact-quick-action__btn--secondary"
                >
                  <DocumentMinusIcon className="fact-quick-action__icon" />
                  <span>Note de credit personnalisee</span>
                </button>
                <p className="fact-quick-action__hint">Sans lien avec une facture</p>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Invoice Modal */}
        <CreateCustomInvoiceModal
          isOpen={showCustomInvoiceModal}
          onClose={() => setShowCustomInvoiceModal(false)}
          onSuccess={() => {
            setShowCustomInvoiceModal(false);
            // Optionally trigger a search to show the new invoice
            handleSearch();
          }}
        />

        {/* Custom Credit Note Modal */}
        <CreateCustomCreditNoteModal
          isOpen={showCustomCreditNoteModal}
          onClose={() => setShowCustomCreditNoteModal(false)}
          onSuccess={() => {
            setShowCustomCreditNoteModal(false);
            // Optionally trigger a search to show the new credit note
            handleSearch();
          }}
        />
      </div>
    );
  }

  // STEP 2: Results - show content based on activeSubTab from parent
  return (
    <div className="fact-recherche-results">
      {/* Content based on sub-tab */}
      <div className="fact-recherche-results-content">
        {activeSubTab === 'factures' ? (
          <InvoicesListView
            initialFilters={filters}
            searchLabel={activeSearchLabel}
            searchContext={
              searchMode !== 'recent' && searchTerm.trim()
                ? { mode: searchMode as 'affaire' | 'client' | 'facture', term: searchTerm.trim() }
                : undefined
            }
          />
        ) : activeSubTab === 'notes-credit' ? (
          <CreditNotesListView
            initialFilters={filters}
            searchLabel={activeSearchLabel}
            searchContext={
              searchMode !== 'recent' && searchTerm.trim()
                ? { mode: searchMode as 'affaire' | 'client' | 'facture', term: searchTerm.trim() }
                : undefined
            }
          />
        ) : (
          /* Produits tab - only shown when searching by affaire */
          <ProductsEmbeddedView
            affaireId={searchTerm.trim()}
            onProductsChanged={() => {
              // Could trigger refresh of invoice eligibility if needed
            }}
          />
        )}
      </div>
    </div>
  );
};

// Also export a hook or ref-based approach for the parent to trigger back
export const useRechercheBack = () => {
  const [shouldGoBack, setShouldGoBack] = useState(false);

  const triggerBack = () => setShouldGoBack(true);
  const resetBack = () => setShouldGoBack(false);

  return { shouldGoBack, triggerBack, resetBack };
};

export default RechercheView;
